/* Making a piece of work: a job with its rows, a request, a department task.
 *
 * Desk first (UX bar): a manager fills the header once and the rows below it,
 * each row with its own people. A row's checker and approver usually repeat,
 * so a new row starts with the last row's. Everything goes in one request, so
 * a job never exists half-made.
 *
 * The fields come from the pack and are drawn by the engine's own controls;
 * the people pickers offer only those who hold the role the step needs.
 */
import { useEffect, useMemo, useState } from 'react'
import { Failed, Loading, NoAccess, ScreenState, useToast } from '@jackdesk/shell'
import { ErpError, newIdempotencyKey } from '../api'
import { useErp } from '../context'
import type { ActivePack, Kind, MakeWorkBody, PackField } from './types'
import { DepartmentPicker, PackFieldInput, PeoplePicker, PlainField, usePersonName } from './Pickers'
import { ProjectForm } from './Projects'
import { usePacksActive } from './usePacks'
import { hasReviewer, homeLabel, whoWord } from './words'

type RowDraft = {
  key: string
  subject: string
  makers: string[]
  checker: string
  /** Optional: naming one sends the row through the review after the checker. */
  reviewer: string
  approver: string
  values: Record<string, unknown>
  description: string
}

const defaults = (fields: PackField[]) =>
  Object.fromEntries(fields.filter((f) => f.default).map((f) => [f.name, f.default]))

/** The row fields a manager fills; the reply's own are left to the person
 *  doing the work. */
function rowFields(kind: Kind): PackField[] {
  const replyNames = new Set([...(kind.reply?.fields ?? []).map((f) => f.name),
                              kind.reply?.reason?.name].filter(Boolean))
  return kind.fields.filter((f) => !replyNames.has(f.name))
}

function newRow(kind: Kind | null, from?: RowDraft): RowDraft {
  return {
    key: Math.random().toString(36).slice(2),
    subject: '', makers: [], description: '',
    checker: from?.checker ?? '', reviewer: from?.reviewer ?? '', approver: from?.approver ?? '',
    values: kind ? defaults(rowFields(kind)) : {},
  }
}

export function MakeWork({ packId, kindKey, project: startProject, onDone, onCancel }: {
  packId: string; kindKey: string
  /** Started from a project's page: the work is for that project. */
  project?: string
  onDone: (name: string) => void; onCancel: () => void
}) {
  const erp = useErp()
  const toast = useToast()
  const { active, failed, reload } = usePacksActive()
  const pack: ActivePack | undefined = active?.packs.find((p) => p.id === packId)
  const kind = pack?.kinds.find((k) => k.key === kindKey) ?? null
  const rowKind = kind?.rows ? pack?.kinds.find((k) => k.key === kind.rows) ?? null : null

  const [key] = useState(newIdempotencyKey)
  const [subject, setSubject] = useState('')
  const [project, setProject] = useState(startProject ?? '')
  const [client, setClient] = useState<string | null>(null)
  const [projectManager, setProjectManager] = useState<string | null>(null)
  const [projectTitle, setProjectTitle] = useState<string | null>(null)
  const [makingProject, setMakingProject] = useState(false)
  const [deadline, setDeadline] = useState('')
  const [description, setDescription] = useState('')
  const [department, setDepartment] = useState('')
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [makers, setMakers] = useState<string[]>([])
  const [approver, setApprover] = useState('')
  const [rows, setRows] = useState<RowDraft[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  // Start from the pack's defaults once the pack has arrived.
  useEffect(() => {
    if (!kind) return
    setValues(defaults(kind.fields))
    if (rowKind) setRows([newRow(rowKind)])
  }, [kind, rowKind])

  // The project's client, so the manager sees whose job this is, and the person who runs it,
  // who approves the rows unless somebody else is chosen.
  useEffect(() => {
    if (!project) { setClient(null); setProjectManager(null); setProjectTitle(null); return }
    const controller = new AbortController()
    erp.workProject(project, controller.signal)
      .then((d) => {
        setClient(d.project.customer || null)
        setProjectManager(d.project.manager || null)
        setProjectTitle(d.project.project_name || null)
      })
      .catch(() => { setClient(null); setProjectManager(null); setProjectTitle(null) })
    return () => controller.abort()
  }, [erp, project])

  // A row with nobody to approve it takes the project's manager.
  useEffect(() => {
    if (!projectManager) return
    setRows((rs) => rs.map((r) => (r.approver ? r : { ...r, approver: projectManager })))
  }, [projectManager])

  const words = kind?.words ?? {}
  const rowWords = rowKind?.words ?? {}
  const rowFieldList = useMemo(() => (rowKind ? rowFields(rowKind) : []), [rowKind])

  if (failed) return <div className="page"><Failed message={failed.message} onRetry={reload} /></div>
  if (!active) return <div className="page"><Loading rows={8} label="Opening the form" /></div>
  if (!pack || !kind) {
    return (
      <div className="page">
        <ScreenState title="That kind of work is not here"
          detail="The pack it belongs to is switched off, or the link is out of date."
          actions={<button className="btn" onClick={onCancel}>Back to {homeLabel(active).toLowerCase()}</button>} />
      </div>
    )
  }
  if (!kind.may_create) {
    return (
      <div className="page">
        <NoAccess what={`making a ${kind.label.toLowerCase()}`}
                  needs={kind.create === 'everyone' ? [] : kind.create} />
      </div>
    )
  }

  const err = (k: string) => errors[k]
  const setRow = (i: number, patch: Partial<RowDraft>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  async function send() {
    if (!kind) return
    setErrors({}); setProblem(null); setSending(true)
    const body: MakeWorkBody = {
      pack: packId, kind: kindKey, subject: subject.trim(),
      ...(project ? { project } : {}),
      ...(deadline ? { exp_end_date: deadline } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(department ? { department } : {}),
      values,
      ...(kind.flow === 'complete' ? { makers } : {}),
      ...(kind.flow === 'decide' ? { approver } : {}),
      ...(rowKind ? {
        rows: rows.map((r) => ({
          subject: r.subject.trim(), makers: r.makers, checker: r.checker, approver: r.approver,
          ...(r.reviewer ? { reviewer: r.reviewer } : {}),
          values: r.values, ...(r.description.trim() ? { description: r.description.trim() } : {}),
        })),
      } : {}),
    }
    try {
      const made = await erp.makeWork(body, key)
      toast('ok', rowKind
        ? `${kind.label} made, with ${made.rows.length} ${rowKind.plural.toLowerCase()} assigned.`
        : `${kind.label} made.`)
      onDone(made.name)
    } catch (e) {
      const error = e as ErpError
      setErrors(error.fields ?? {})
      setProblem(error.message)
    } finally {
      setSending(false)
    }
  }

  const reviewing = hasReviewer(rowKind)
  const managerRoles = pack.project?.manager_roles ?? []
  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 1080 }}>
      <div className="row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onCancel}>← {homeLabel(active)}</button>
          <h1 style={{ marginTop: 6 }}>New {kind.label.toLowerCase()}</h1>
          <p className="muted small" style={{ marginTop: 4, maxWidth: 720 }}>{kind.about || pack.title}</p>
        </div>
      </div>

      {/* Outside the job's own form: forms cannot hold forms. */}
      {makingProject ? (
        <section className="card card-pad" aria-label="New project" style={{ borderColor: 'var(--accent-line)' }}>
          <ProjectForm managerRoles={managerRoles}
            onDone={(name) => { setProject(name); setMakingProject(false) }}
            onCancel={() => setMakingProject(false)} />
        </section>
      ) : null}

    <form style={{ display: 'grid', gap: 16 }}
          onSubmit={(e) => { e.preventDefault(); void send() }} noValidate>

      {problem ? (
        <div className="card card-pad" role="alert" style={{ borderColor: 'var(--danger)' }}>
          <b>{problem}</b>
          {Object.keys(errors).length ? <div className="small muted">The fields marked below need a look.</div> : null}
        </div>
      ) : null}

      <section className="card card-pad" style={{ display: 'grid', gap: 12 }} aria-label="About it">
        <div className="field">
          <label htmlFor="mw_subject">Title<span aria-hidden="true"> *</span></label>
          <input id="mw_subject" className="input" value={subject} autoFocus
                 aria-invalid={err('subject') ? true : undefined}
                 onChange={(e) => setSubject(e.target.value)} />
          {err('subject') ? <div className="small" style={{ color: 'var(--danger)' }}>{err('subject')}</div> : null}
        </div>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          {kind.project ? (
            <div>
              <PlainField fieldname="project" label="Project" fieldtype="Link" options="Project"
                          value={project} onChange={(v) => setProject(String(v ?? ''))}
                          required={kind.project === 'required'} error={err('project')} />
              {projectTitle || client || projectManager ? (
                // The project as people know it: its title, not its code (owner, 2026-10-08).
                <div className="small muted">
                  {projectTitle ? <b>{projectTitle}</b> : null}
                  {client ? <div>Client: {client}</div> : null}
                  {projectManager ? <div>Run by <PersonName email={projectManager} /></div> : null}
                </div>
              ) : null}
              {pack.project?.may_create && !makingProject ? (
                <button type="button" className="btn btn-quiet btn-sm" onClick={() => setMakingProject(true)}>
                  + New project
                </button>
              ) : null}
            </div>
          ) : null}
          <PlainField fieldname="exp_end_date" label={words.deadline ?? 'Deadline'} fieldtype="Date"
                      value={deadline} onChange={(v) => setDeadline(String(v ?? ''))}
                      error={err('exp_end_date')} />
          {kind.departments.length ? (
            <DepartmentPicker id="mw_department" value={department} only={kind.departments}
                              onChange={(v) => { setDepartment(v); setMakers([]) }}
                              error={err('department')} />
          ) : null}
          {kind.fields.map((f) => (
            <PackFieldInput key={f.name} field={f} value={values[f.name] ?? ''}
                            error={err(f.name)}
                            onChange={(v) => setValues((vs) => ({ ...vs, [f.name]: v }))} />
          ))}
          {kind.flow === 'decide' ? (
            <PeoplePicker id="mw_approver" label={words.approver ?? whoWord('approver')}
                          role={kind.roles.approver} value={approver ? [approver] : []}
                          onChange={(v) => setApprover(v[0] ?? '')} required error={err('approver')}
                          hint="The person who decides. Only they can approve or reject it." />
          ) : null}
          {kind.flow === 'complete' ? (
            <PeoplePicker id="mw_makers" label={words.makers ?? 'Assign to'} multiple
                          role={kind.roles.assignee} department={department || undefined}
                          value={makers} onChange={setMakers} required error={err('makers')}
                          hint={department ? 'People in this department.' : kind.departments.length
                            ? 'Choose the department first to see its people.' : undefined} />
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="mw_description">{words.description ?? 'Notes'}</label>
          <textarea id="mw_description" className="textarea" rows={3} value={description}
                    onChange={(e) => setDescription(e.target.value)} />
        </div>
      </section>

      {rowKind ? (
        <section style={{ display: 'grid', gap: 10 }} aria-label={words.rows ?? rowKind.plural}>
          <div className="row row-between">
            <h2 style={{ margin: 0 }}>{words.rows ?? rowKind.plural}</h2>
            {err('rows') ? <span className="small" style={{ color: 'var(--danger)' }}>{err('rows')}</span> : null}
          </div>
          {rows.map((r, i) => (
            <div key={r.key} className="card card-pad" style={{ display: 'grid', gap: 10 }}
                 aria-label={`${rowKind.label} ${i + 1}`}>
              <div className="row row-between">
                <strong className="small">{rowKind.label} {i + 1}</strong>
                {rows.length > 1 ? (
                  <button type="button" className="btn btn-quiet btn-sm"
                          onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>Remove</button>
                ) : null}
              </div>
              <div className="field">
                <label htmlFor={`row_${r.key}_subject`}>Description<span aria-hidden="true"> *</span></label>
                <input id={`row_${r.key}_subject`} className="input" value={r.subject}
                       aria-invalid={err(`rows.${i}.subject`) ? true : undefined}
                       onChange={(e) => setRow(i, { subject: e.target.value })} />
                {err(`rows.${i}.subject`) ? <div className="small" style={{ color: 'var(--danger)' }}>{err(`rows.${i}.subject`)}</div> : null}
              </div>
              <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <PeoplePicker id={`row_${r.key}_makers`} label={rowWords.makers ?? whoWord('maker', rowWords)}
                              multiple role={rowKind.roles.maker} value={r.makers}
                              onChange={(v) => setRow(i, { makers: v })} required
                              error={err(`rows.${i}.makers`)} />
                <PeoplePicker id={`row_${r.key}_checker`} label={whoWord('checker', rowWords)}
                              role={rowKind.roles.checker} value={r.checker ? [r.checker] : []}
                              onChange={(v) => setRow(i, { checker: v[0] ?? '' })} required
                              error={err(`rows.${i}.checker`)} />
                {reviewing ? (
                  <PeoplePicker id={`row_${r.key}_reviewer`} label={`${whoWord('reviewer', rowWords)} (optional)`}
                                role={rowKind.roles.reviewer} value={r.reviewer ? [r.reviewer] : []}
                                onChange={(v) => setRow(i, { reviewer: v[0] ?? '' })}
                                error={err(`rows.${i}.reviewer`)}
                                hint={`Checks after the ${whoWord('checker', rowWords).toLowerCase()}, before the ${whoWord('approver', rowWords)}.`} />
                ) : null}
                <PeoplePicker id={`row_${r.key}_approver`} label={whoWord('approver', rowWords)}
                              role={rowKind.roles.approver} value={r.approver ? [r.approver] : []}
                              onChange={(v) => setRow(i, { approver: v[0] ?? '' })} required
                              error={err(`rows.${i}.approver`)} />
                {rowFieldList.map((f) => (
                  <PackFieldInput key={f.name} field={{ ...f, fieldname: `${f.fieldname}_${r.key}` }}
                                  value={r.values[f.name] ?? ''}
                                  onChange={(v) => setRow(i, { values: { ...r.values, [f.name]: v } })} />
                ))}
              </div>
              <div className="field">
                <label htmlFor={`row_${r.key}_notes`}>{rowWords.description ?? 'Notes'}</label>
                <input id={`row_${r.key}_notes`} className="input" value={r.description}
                       onChange={(e) => setRow(i, { description: e.target.value })} />
              </div>
            </div>
          ))}
          <div>
            <button type="button" className="btn"
                    onClick={() => setRows((rs) => [...rs, newRow(rowKind, rs[rs.length - 1])])}>
              Add another {rowKind.label.toLowerCase()}
            </button>
          </div>
        </section>
      ) : null}

      <div className="row" style={{ gap: 8, position: 'sticky', bottom: 0, padding: '10px 0',
                                    background: 'var(--ground)' }}>
        <button type="submit" className="btn btn-primary" disabled={sending}>
          {sending ? 'Sending…' : rowKind ? 'Create and assign' : `Create ${kind.label.toLowerCase()}`}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={sending}>Cancel</button>
      </div>
    </form>
    </div>
  )
}

function PersonName({ email }: { email: string }) {
  const nameOf = usePersonName()
  return <>{nameOf(email)}</>
}
